// @flow
import React, { useContext } from 'react';
import { getIn, useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';

import {
  CLUSTER_RTCE_FORM,
  CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_RTCE_STORAGE_TYPE_BACKEND_NAME,
  RTCE_NUM_CCUS_BACKEND_NAME,
  RTCE_STORAGE_GB_BACKEND_NAME,
  RTCE_STORAGE_TYPE_CONFLUENT,
  RTCE_STORAGE_TYPE_OWN,
} from '../../../constants';
import { ClusterInputTable } from '../cluster-input-table/ClusterInputTable';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { toastError } from '../../presentational/notifications/utils';
import { useUpdateClusterMutation } from '../../../service-definitions/estimates';
import { EstimateContext } from '../../../contexts/EstimateContext';
import { isAnythingEditedOnClusterPageBesidesTheGivenForm } from '../../views/utils';
import { SelectField } from '../../../formik-utils/FormFields';
import { Spacer } from '../../presentational/spacing/Spacer';
import { Tooltip } from '../../presentational/Tooltip';
import { StyledSegmentWithBorderColor } from '../../../common-utils/styledComponents';

import { getIfRTCEConfluentStorageIsEnabledFromFormikValues } from './utils';

const RTCE_ESTIMATED_STORAGE_TOOLTIP_MESSAGE = 'Estimated materialized storage in GB.';

export const RTCEMonthlyUsageContainer = () => {
  const rowsConfig = [
    {
      backendName: 'is_enabled',
      displayName: 'Active',
      isBooleanField: true,
    },
    {
      backendName: RTCE_NUM_CCUS_BACKEND_NAME,
      displayName: 'CCUs',
    },
    {
      backendName: RTCE_STORAGE_GB_BACKEND_NAME,
      displayName: (
        <>
          Estimated Storage (GB)
          <Tooltip text={RTCE_ESTIMATED_STORAGE_TOOLTIP_MESSAGE} />
        </>
      ),
      displayNameForDownloads: 'Estimated Storage (GB)',
      // Only relevant when Storage Type is "Enable Confluent Storage"; hidden under "Use my own storage".
      showOrHideFunc: (values) => getIfRTCEConfluentStorageIsEnabledFromFormikValues(values),
    },
  ];

  const storageTypeOptions = [
    {
      name: RTCE_STORAGE_TYPE_CONFLUENT,
      display_name: 'Enable Confluent Storage',
    },
    {
      name: RTCE_STORAGE_TYPE_OWN,
      display_name: 'Use my own storage',
    },
  ];

  const { estimateId, clusterId } = useParams();
  const [updateCluster] = useUpdateClusterMutation();
  const estimate = useContext(EstimateContext);

  const { values, initialValues } = useFormikContext();
  const shouldFormBeDisabled = isAnythingEditedOnClusterPageBesidesTheGivenForm(
    values,
    initialValues,
    CLUSTER_RTCE_FORM
  );

  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <ClusterInputTable
        AdditionalComponent={
          <>
            <Spacer y={40} />
            <StyledSegmentWithBorderColor compact={true}>
              <SelectField
                disabled={shouldFormBeDisabled}
                fieldDisplayName="Storage Type"
                fieldName={`${CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG}.${CLUSTER_RTCE_STORAGE_TYPE_BACKEND_NAME}`}
                fluid={false}
                options={storageTypeOptions}
                search={false}
              />
            </StyledSegmentWithBorderColor>
          </>
        }
        disabled={shouldFormBeDisabled}
        keyNameInValues={CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG}
        rowsConfig={rowsConfig}
        shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled={true}
        tableName="RTCE Monthly Usage"
        updateFunc={async () => {
          const valuesToSendToBackend = getIn(values, CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG);
          const storageType = valuesToSendToBackend.storage_type;
          const rtceInputs = Object.values(valuesToSendToBackend.months);

          const payload = {
            id: clusterId,
            estimate_id: estimateId,
            estimate_version: estimate?.inputs?.estimate_version,
            rtce: {
              storage_type: storageType,
              inputs: rtceInputs,
            },
          };
          const { error } = await updateCluster({
            estimateId,
            clusterId,
            payload,
          });
          if (error) {
            toastError(error);
          }
        }}
      />
    </PaddedAndRaisedSegment>
  );
};
